"use client";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAnalytics } from "./useAnalytics";
import { ANALYTICS_EVENTS } from "./events";
import {
  buildDashboardEventProps,
  buildCreatorOnboardingEventProps,
  buildLogoutEventProps,
  buildProfileShareEventProps,
  buildCopyLinkEventProps,
  buildProfileRedirectionEventProps,
  buildSeoEditEventProps,
  buildShareProfileEditEventProps,
  buildJobShareEventProps,
  buildDeleteAccountEventProps,
  buildLinkAccountEventProps,
  buildEditProfileEventProps,
  buildEditProfilePicEventProps,
  buildEditProfileNameEventProps,
  buildEditProfileBioEventProps,
  buildEditProfileNicheEventProps,
  buildEditProfileSocialLinkEventProps,
  buildEditProfileLinkEventProps,
  buildDesignStartEventProps,
  buildDesignEditEventProps,
  buildSettingsEventProps,
  buildSettingsNicheEventProps,
  buildSettingsKeywordEventProps,
  buildSettingsCountryEventProps,
  buildSettingsIndustryEventProps,
  buildSettingsEmpKeywordEventProps,
  buildFilterEventProps,
  buildFilterJobsEventProps,
  buildFilterCompanyEventProps,
  buildEarningEventProps,
  buildPayoutDownloadEventProps,
  buildPayoutAddedEventProps,
  buildRevenueEventProps,
  buildPostStudioOpenedEventProps,
  buildPostStudioJobsSelectEventProps,
  buildPostStudioPlatformEventProps,
  buildPostStudioRegenerateEventProps,
  buildPostStudioShareEventProps,
} from "./helpers";
import {
  isFirstProfileShare,
  markProfileShareCompleted,
  isFirstJobShare,
  markJobShareCompleted,
} from "./firstTimeTracker";
import {
  markFirstJobShare as markFirstJobShareApi,
  markJobShareSuccess as markJobShareSuccessApi,
  trackJobShareClick as trackJobShareClickApi,
} from "@/modules/creator/services/creatorClient";
import { queryKeys } from "@/lib/queryKeys";
import type { ShareType, SourceScreen, DesignChangeType } from "@artha/types";

/**
 * Custom hook for creator analytics events
 * Provides clean, type-safe methods for tracking creator actions
 */
export function useCreatorAnalytics() {
  const { trackEvent, reset, updateUserProperties } = useAnalytics();
  const queryClient = useQueryClient();

  /**
   * Track CR_DASHBOARD event - when creator clicks "Go to Dashboard" after approval
   */
  const trackDashboard = useCallback(
    (creator?: any) => {
      const props = buildDashboardEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_DASHBOARD, props);
    },
    [trackEvent]
  );

  const trackOnboardingWhatsApp = useCallback(
    (creator?: any) => {
      const props = buildCreatorOnboardingEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_ONBOARDING_WHATSAPP, props);
    },
    [trackEvent]
  );

  const trackOnboardingShareJob = useCallback(
    (creator?: any) => {
      const props = buildCreatorOnboardingEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_ONBOARDING_SHAREJOB, props);
    },
    [trackEvent]
  );

  const trackOnboardingPostStudio = useCallback(
    (creator?: any) => {
      const props = buildCreatorOnboardingEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_ONBOARDING_POSTSTUDIO, props);
    },
    [trackEvent]
  );

  const trackOnboardingComplete = useCallback(
    (creator?: any) => {
      const props = buildCreatorOnboardingEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_ONBOARDING_COMPLETE, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_LOGOUT event - when creator logs out
   */
  const trackLogout = useCallback(
    async (sessionDuration?: number, creator?: any) => {
      const props = buildLogoutEventProps(sessionDuration, creator);
      trackEvent(ANALYTICS_EVENTS.CR_LOGOUT, props);
      await reset();
    },
    [trackEvent, reset]
  );

  /**
   * Track profile share - automatically handles first-time vs subsequent
   */
  const trackProfileShare = useCallback(
    (
      data: { share_type: ShareType; source_screen: SourceScreen },
      creator?: any
    ) => {
      const props = buildProfileShareEventProps(data, creator);

      if (isFirstProfileShare()) {
        trackEvent(ANALYTICS_EVENTS.CR_FIRST_PROFILE_SHARE, props);
        markProfileShareCompleted();
      } else {
        trackEvent(ANALYTICS_EVENTS.CR_PROFILE_SHARE, props);
      }
    },
    [trackEvent]
  );

  /**
   * Track CR_SHARE_PROFILE_COPYLINK event - when creator copies artha.link
   */
  const trackCopyLink = useCallback(
    (sourceScreen: SourceScreen, creator?: any) => {
      const props = buildCopyLinkEventProps(sourceScreen, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SHARE_PROFILE_COPYLINK, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SHARE_PROFILE_REDIRECTION event - when creator clicks social media to add bio link
   */
  const trackProfileRedirection = useCallback(
    (linkedAccount: string, creator?: any) => {
      const props = buildProfileRedirectionEventProps(linkedAccount, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SHARE_PROFILE_REDIRECTION, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SEO_EDIT event - when creator accesses SEO settings
   */
  const trackSeoEdit = useCallback(
    (settings?: string, creator?: any) => {
      const props = buildSeoEditEventProps(settings, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SEO_EDIT, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SHARE_PROFILE_EDIT event - when creator edits profile from share sheet
   */
  const trackShareProfileEdit = useCallback(
    (creator?: any) => {
      const props = buildShareProfileEditEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_SHARE_PROFILE_EDIT, props);
    },
    [trackEvent]
  );

  /**
   * Track job share - automatically handles first-time vs subsequent
   */
  const trackJobShare = useCallback(
    async (
      data: {
        social_media: string;
        job_id: string;
        job_title: string;
        job_company: string;
        job_cpa_value?: number;
        job_cpc_value?: number;
        is_hot?: boolean;
      },
      creator?: any
    ) => {
      const props = buildJobShareEventProps(data, creator);
      let isFirst = false;

      try {
        const result = await markFirstJobShareApi();
        isFirst = result.isFirst;
      } catch {
        if (isFirstJobShare()) {
          isFirst = true;
          markJobShareCompleted();
        }
      }

      try {
        await markJobShareSuccessApi();
        void queryClient.invalidateQueries({
          queryKey: queryKeys.revenue.dashboardMetrics(),
        });
      } catch {
        // No-op: share tracking should still emit analytics even if the banner timestamp fails.
      }

      if (isFirst) {
        trackEvent(ANALYTICS_EVENTS.CR_SHARE_FIRST_JOB, props);
      } else {
        trackEvent(ANALYTICS_EVENTS.CR_SHARE_JOB, props);
      }
    },
    [queryClient, trackEvent]
  );

  /**
   * Track creator share-icon click counters and mirror them to person properties.
   */
  const trackJobShareIconClick = useCallback(
    async (creatorId?: string, isHot?: boolean) => {
      if (!creatorId) return;

      try {
        const result = await trackJobShareClickApi();
        trackEvent(ANALYTICS_EVENTS.CR_SHARE_JOB, {
          social_media: "share_icon",
          job_id: "",
          job_title: "",
          job_company: "",
          weekly_job_share_count: result.weeklyJobShareCount,
          monthly_job_share_count: result.monthlyJobShareCount,
          ...(isHot && { is_hot: true }),
        });
        updateUserProperties(creatorId, {
          weekly_job_share_count: result.weeklyJobShareCount,
          monthly_job_share_count: result.monthlyJobShareCount,
        });
      } catch {
        // No-op: share flow should stay responsive even when analytics fails.
      }
    },
    [trackEvent, updateUserProperties]
  );

  /**
   * Track CR_DELETE_ACCOUNT event - when creator deletes account
   */
  const trackDeleteAccount = useCallback(
    (reason?: string, creator?: any) => {
      const props = buildDeleteAccountEventProps(reason, creator);
      trackEvent(ANALYTICS_EVENTS.CR_DELETE_ACCOUNT, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_LINK_ACCOUNT event - when creator links additional social account
   */
  const trackLinkAccount = useCallback(
    (
      data: {
        social_media: string;
        source_screen: SourceScreen;
        linked_accounts: string[];
      },
      creator?: any
    ) => {
      const props = buildLinkAccountEventProps(data, creator);
      trackEvent(ANALYTICS_EVENTS.CR_LINK_ACCOUNT, props);
    },
    [trackEvent]
  );

  // ============================================
  // My Link - Profile Edit Tracking
  // ============================================

  /**
   * Track CR_EDIT_PROFILE event - when creator clicks on profile in my link
   */
  const trackEditProfile = useCallback(
    (creator?: any) => {
      const props = buildEditProfileEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_EDIT_PROFILE, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_EDIT_PROFILE_PIC event - when creator changes profile pic successfully
   */
  const trackEditProfilePic = useCallback(
    (creator?: any) => {
      const props = buildEditProfilePicEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_EDIT_PROFILE_PIC, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_EDIT_PROFILE_NAME event - when creator changes name
   */
  const trackEditProfileName = useCallback(
    (newName: string, creator?: any) => {
      const props = buildEditProfileNameEventProps(newName, creator);
      trackEvent(ANALYTICS_EVENTS.CR_EDIT_PROFILE_NAME, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_EDIT_PROFILE_LINK event - when creator changes artha link
   */
  const trackEditProfileLink = useCallback(
    (username: string, creator?: any) => {
      const props = buildEditProfileLinkEventProps(username, creator);
      trackEvent(ANALYTICS_EVENTS.CR_EDIT_PROFILE_LINK, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_EDIT_PROFILE_BIO event - when creator changes bio
   */
  const trackEditProfileBio = useCallback(
    (creator?: any) => {
      const props = buildEditProfileBioEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_EDIT_PROFILE_BIO, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_EDIT_PROFILE_NICHE event - when creator changes niches
   */
  const trackEditProfileNiche = useCallback(
    (niches: string[], creator?: any) => {
      const props = buildEditProfileNicheEventProps(niches, creator);
      trackEvent(ANALYTICS_EVENTS.CR_EDIT_PROFILE_NICHE, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_EDIT_PROFILE_SOCIALLINK event - when creator clicks on social links
   */
  const trackEditProfileSocialLink = useCallback(
    (socialMedia: string, creator?: any) => {
      const props = buildEditProfileSocialLinkEventProps(socialMedia, creator);
      trackEvent(ANALYTICS_EVENTS.CR_EDIT_PROFILE_SOCIALLINK, props);
    },
    [trackEvent]
  );

  // ============================================
  // My Link - Design Tracking
  // ============================================

  /**
   * Track CR_DESIGN_START event - when creator clicks on Design
   */
  const trackDesignStart = useCallback(
    (creator?: any) => {
      const props = buildDesignStartEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_DESIGN_START, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_DESIGN_EDIT event - when creator changes design property
   */
  const trackDesignEdit = useCallback(
    (designChange: DesignChangeType, creator?: any) => {
      const props = buildDesignEditEventProps(designChange, creator);
      trackEvent(ANALYTICS_EVENTS.CR_DESIGN_EDIT, props);
    },
    [trackEvent]
  );

  // ============================================
  // My Link - Settings Tracking
  // ============================================

  /**
   * Track CR_SETTINGS event - when creator clicks on settings
   */
  const trackSettings = useCallback(
    (creator?: any) => {
      const props = buildSettingsEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_NICHE_ADD event - when creator adds a niche in settings
   */
  const trackSettingsNicheAdd = useCallback(
    (niches: string[], creator?: any) => {
      const props = buildSettingsNicheEventProps(niches, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_NICHE_ADD, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_NICHE_REMOVE event - when creator removes a niche in settings
   */
  const trackSettingsNicheRemove = useCallback(
    (niches: string[], creator?: any) => {
      const props = buildSettingsNicheEventProps(niches, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_NICHE_REMOVE, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_JOBKW_ADD event - when creator adds a keyword in settings
   */
  const trackSettingsJobKeywordAdd = useCallback(
    (keywords: string[], creator?: any) => {
      const props = buildSettingsKeywordEventProps(keywords, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_JOBKW_ADD, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_JOBKW_REMOVE event - when creator removes a keyword in settings
   */
  const trackSettingsJobKeywordRemove = useCallback(
    (keywords: string[], creator?: any) => {
      const props = buildSettingsKeywordEventProps(keywords, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_JOBKW_REMOVE, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_COUNTRY_ADD event - when creator adds a country in settings
   */
  const trackSettingsCountryAdd = useCallback(
    (country: string, creator?: any) => {
      const props = buildSettingsCountryEventProps(country, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_COUNTRY_ADD, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_COUNTRY_REMOVE event - when creator removes a country in settings
   */
  const trackSettingsCountryRemove = useCallback(
    (country: string, creator?: any) => {
      const props = buildSettingsCountryEventProps(country, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_COUNTRY_REMOVE, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_EMPIND_ADD event - when creator adds an industry in settings
   */
  const trackSettingsIndustryAdd = useCallback(
    (industry: string, creator?: any) => {
      const props = buildSettingsIndustryEventProps(industry, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_EMPIND_ADD, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_EMPIND_REMOVE event - when creator removes an industry in settings
   */
  const trackSettingsIndustryRemove = useCallback(
    (industry: string, creator?: any) => {
      const props = buildSettingsIndustryEventProps(industry, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_EMPIND_REMOVE, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_EMPKW_ADD event - when creator adds an employer keyword
   */
  const trackSettingsEmpKeywordAdd = useCallback(
    (keywords: string[], creator?: any) => {
      const props = buildSettingsEmpKeywordEventProps(keywords, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_EMPKW_ADD, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_SETTINGS_EMPKW_REMOVE event - when creator removes an employer keyword
   */
  const trackSettingsEmpKeywordRemove = useCallback(
    (keywords: string[], creator?: any) => {
      const props = buildSettingsEmpKeywordEventProps(keywords, creator);
      trackEvent(ANALYTICS_EVENTS.CR_SETTINGS_EMPKW_REMOVE, props);
    },
    [trackEvent]
  );

  // ============================================
  // My Link - Filter Tracking
  // ============================================

  /**
   * Track CR_FILTER event - when creator clicks on filter icon
   */
  const trackFilter = useCallback(
    (creator?: any) => {
      const props = buildFilterEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_FILTER, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_FILTER_JOBS event - when creator applies job filters
   */
  const trackFilterJobs = useCallback(
    (filtersChanged: string[], filtersAppliedCount: number, creator?: any) => {
      const props = buildFilterJobsEventProps(filtersChanged, filtersAppliedCount, creator);
      trackEvent(ANALYTICS_EVENTS.CR_FILTER_JOBS, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_FILTER_COMPANY event - when creator applies company filters
   */
  const trackFilterCompany = useCallback(
    (filtersChanged: string[], filtersAppliedCount: number, creator?: any) => {
      const props = buildFilterCompanyEventProps(filtersChanged, filtersAppliedCount, creator);
      trackEvent(ANALYTICS_EVENTS.CR_FILTER_COMPANY, props);
    },
    [trackEvent]
  );

  // ============================================
  // Earnings & Revenue Tracking
  // ============================================

  const trackEarning = useCallback(
    (creator?: any) => {
      const props = buildEarningEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_EARNING, props);
    },
    [trackEvent]
  );

  const trackPayoutDownload = useCallback(
    (creator?: any) => {
      const props = buildPayoutDownloadEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_PAYOUT_DOWNLOAD, props);
    },
    [trackEvent]
  );

  const trackPayoutAdded = useCallback(
    (bankConnected: boolean, creator?: any) => {
      const props = buildPayoutAddedEventProps(bankConnected, creator);
      trackEvent(ANALYTICS_EVENTS.CR_PAYOUT_ADDED, props);
    },
    [trackEvent]
  );

  const trackFirstRevenue = useCallback(
    (data: { lifetime_revenue?: number; weekly_revenue?: number }, creator?: any) => {
      const props = buildRevenueEventProps(data, creator);
      trackEvent(ANALYTICS_EVENTS.CR_FIRST_REVENUE, props);
    },
    [trackEvent]
  );

  const trackRevenueUpdate = useCallback(
    (data: { lifetime_revenue?: number; weekly_revenue?: number }, creator?: any) => {
      const props = buildRevenueEventProps(data, creator);
      trackEvent(ANALYTICS_EVENTS.CR_REVENUE_UPDATE, props);
    },
    [trackEvent]
  );

  // ============================================
  // Post Studio Tracking
  // ============================================

  /**
   * Track CR_POSTSTUDIO event - when creator opens post studio
   */
  const trackPostStudioOpened = useCallback(
    (creator?: any) => {
      const props = buildPostStudioOpenedEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_POSTSTUDIO, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_POSTSTUDIO_JOBSSELECT event - when creator selects jobs
   */
  const trackPostStudioJobsSelect = useCallback(
    (creator?: any) => {
      const props = buildPostStudioJobsSelectEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_POSTSTUDIO_JOBSSELECT, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_POSTSTUDIO_PLATFORM event - when creator chooses platform
   */
  const trackPostStudioPlatform = useCallback(
    (platform: string, creator?: any) => {
      const props = buildPostStudioPlatformEventProps(platform, creator);
      trackEvent(ANALYTICS_EVENTS.CR_POSTSTUDIO_PLATFORM, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_POSTSTUDIO_REGENERATE event - when creator regenerates content
   */
  const trackPostStudioRegenerate = useCallback(
    (creator?: any) => {
      const props = buildPostStudioRegenerateEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_POSTSTUDIO_REGENERATE, props);
    },
    [trackEvent]
  );

  /**
   * Track CR_POSTSTUDIO_SHARE event - when creator clicks copy & go
   */
  const trackPostStudioShare = useCallback(
    (creator?: any) => {
      const props = buildPostStudioShareEventProps(creator);
      trackEvent(ANALYTICS_EVENTS.CR_POSTSTUDIO_SHARE, props);
    },
    [trackEvent]
  );

  return {
    trackDashboard,
    trackOnboardingWhatsApp,
    trackOnboardingShareJob,
    trackOnboardingPostStudio,
    trackOnboardingComplete,
    trackLogout,
    trackProfileShare,
    trackCopyLink,
    trackProfileRedirection,
    trackSeoEdit,
    trackShareProfileEdit,
    trackJobShare,
    trackJobShareIconClick,
    trackDeleteAccount,
    trackLinkAccount,
    // My Link - Profile Edit
    trackEditProfile,
    trackEditProfilePic,
    trackEditProfileName,
    trackEditProfileLink,
    trackEditProfileBio,
    trackEditProfileNiche,
    trackEditProfileSocialLink,
    // My Link - Design
    trackDesignStart,
    trackDesignEdit,
    // My Link - Settings
    trackSettings,
    trackSettingsNicheAdd,
    trackSettingsNicheRemove,
    trackSettingsJobKeywordAdd,
    trackSettingsJobKeywordRemove,
    trackSettingsCountryAdd,
    trackSettingsCountryRemove,
    trackSettingsIndustryAdd,
    trackSettingsIndustryRemove,
    trackSettingsEmpKeywordAdd,
    trackSettingsEmpKeywordRemove,
    // My Link - Filters
    trackFilter,
    trackFilterJobs,
    trackFilterCompany,
    // Earnings & Revenue
    trackEarning,
    trackPayoutDownload,
    trackPayoutAdded,
    trackFirstRevenue,
    trackRevenueUpdate,
    // Post Studio
    trackPostStudioOpened,
    trackPostStudioJobsSelect,
    trackPostStudioPlatform,
    trackPostStudioRegenerate,
    trackPostStudioShare,
  };
}
